import { getAuthenticatedHeaders } from './apiHeaders';
import { ApiError } from './apiError';
import type { ProblemDetails } from './apiError';

/**
 * A thin wrapper around `fetch` that:
 *  1. Automatically attaches authenticated headers (EVO Bearer token + API key).
 *  2. Throws an `ApiError` with the HTTP status when the response is not OK,
 *     making it easy for the global React Query error handler to detect 401/403.
 *
 * Callers can pass additional `RequestInit` options which are merged with
 * the authenticated headers (caller-supplied headers take precedence).
 */
export async function authenticatedFetch(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  const authHeaders = await getAuthenticatedHeaders();

  const response = await fetch(url, {
    ...options,
    headers: {
      ...authHeaders,
      ...(options.headers as Record<string, string> | undefined),
    },
  });

  if (!response.ok) {
    let message = `HTTP error! status: ${response.status}`;
    let problemDetails: ProblemDetails | undefined;
    try {
      const body = await response.json();
      // Support RFC 7807 Problem Details format (title/detail) from the API
      if (body?.title || body?.detail) {
        problemDetails = {
          type: body.type,
          title: body.title,
          status: body.status,
          detail: body.detail,
          instance: body.instance,
          traceId: body.traceId,
          errorCode: body.errorCode,
          retryAfter: body.retryAfter,
          platformName: body.platformName,
        };
        message = body.detail ?? body.title;
      } else if (body?.error) {
        message = body.error;
      }
    } catch {
      // response body was not JSON — keep the default message
    }
    throw new ApiError(response.status, message, problemDetails);
  }

  return response;
}
